import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchData } from "./config";

export interface skills {
  id: number;
  skill: string;
  slug: string;
  description: string | null;
  id_encrypted: string;
  pivot: {
    id_role: number;
    id_skill: number;
  };
}

export interface roles {
  id: number;
  role: string;
  slug: string;
  description: string | null;
  id_encrypted: string;
  skills: skills[];
  status: string;
}

interface state {
  status: string;
  roles: roles[];
  skills: skills[];
  next_page_url: string;
  prev_page_url: string;
  current_page: number;
}

const initialState: state = {
  status: "",
  roles: [],
  skills: [],
  next_page_url: "",
  prev_page_url: "",
  current_page: 1,
};
interface Pagination {
  page?: number;
  search?: string;
}

export const getRoles = createAsyncThunk<any, Pagination>(
  "roles/getRoles",
  async ({ page, search }) => {
    if (search !== undefined) {
      const response = await fetchData().get(
        `/api/guardian/roles?rows=10&page=${page}&search_full=${search}&order=ASC`,
      );
      const dataResponse = (await response).data;
      return dataResponse;
    } else if (page !== undefined) {
      const response = await fetchData().get(
        `/api/guardian/roles?rows=10&page=${page}&order=ASC`,
      );
      const dataResponse = (await response).data;
      // console.log(dataResponse);
      return dataResponse;
    } else {
      const response = await fetchData().get(`/api/guardian/roles?order=ASC`);
      const dataResponse = (await response).data;
      return dataResponse;
    }
  },
);

interface NewRole {
  data: any;
}

export const newRole = createAsyncThunk<any, NewRole>(
  "roles/newRole",
  async ({ data }) => {
    const response = await fetchData().post(`/api/guardian/roles`, data);
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

interface UpdateRole {
  id_encrypted: string;
  data: any;
}

export const updateRole = createAsyncThunk<any, UpdateRole>(
  "roles/updateRole",
  async ({ data, id_encrypted }) => {
    const response = await fetchData().put(
      `/api/guardian/roles/${id_encrypted}`,
      data,
    );
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

interface DeleteRole {
  id_encrypted: string;
}

export const deleteRole = createAsyncThunk<any, DeleteRole>(
  "roles/deleteRole",
  async ({ id_encrypted }) => {
    const response = await fetchData().delete(
      `/api/guardian/roles/${id_encrypted}`,
    );
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

interface AddSkills {
  id_role: string;
  id_skill: any;
}

export const addSkillsRole = createAsyncThunk<any, AddSkills>(
  "roles/addRoleSkills",
  async (data) => {
    // console.log(data);
    const response = await fetchData().post(`/api/guardian/role_skills`, data);
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

export const getSkills = createAsyncThunk<any, Pagination>(
  "roles/getSkills",
  async ({ page, search }) => {
    if (search !== undefined) {
      const response = await fetchData().get(
        `/api/guardian/skills?rows=10&page=${page}&search_full=${search}&order=ASC`,
      );
      const dataResponse = (await response).data;
      return dataResponse;
    } else {
      const response = await fetchData().get(
        `/api/guardian/skills?rows=10&page=${page}&order=ASC`,
      );
      const dataResponse = (await response).data;
      return dataResponse;
    }
  },
);

interface UpdateProps {
  id_encrypted: string;
  data: any;
}

export const updateSkills = createAsyncThunk<any, UpdateProps>(
  "roles/updateSkills",
  async ({ id_encrypted, data }) => {
    const response = await fetchData().put(
      `/api/guardian/skills/${id_encrypted}`,
      data,
    );
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

interface NewSkill {
  data: any;
}

export const createSkill = createAsyncThunk<any, NewSkill>(
  "roles/createSkill",
  async ({ data }) => {
    const response = await fetchData().post(`/api/guardian/skills`, data);
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

interface DeleteSkill {
  id_encrypted: string;
}

export const deleteSkill = createAsyncThunk<any, DeleteSkill>(
  "roles/deleteSkill",
  async ({ id_encrypted }) => {
    const response = await fetchData().delete(
      `/api/guardian/skills/${id_encrypted}`,
    );
    const dataResponse = (await response).data;
    return dataResponse;
  },
);

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    resetStatus: (state) => {
      state.status = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRoles.pending, (state) => {
      state.status = "getRoles_pending";
    });
    builder.addCase(getRoles.fulfilled, (state, { payload }) => {
      state.status = "getRoles_success";
      if (payload.data.data !== undefined) {
        state.roles = payload.data.data;
        state.next_page_url = payload.data.next_page_url;
        state.prev_page_url = payload.data.prev_page_url;
        state.current_page = payload.data.current_page;
      } else {
        state.roles = payload.data;
      }
    });
    builder.addCase(getRoles.rejected, (state) => {
      state.status = "getRoles_rejected";
    });
    builder.addCase(getSkills.pending, (state) => {
      state.status = "getSkills_pending";
    });
    builder.addCase(getSkills.fulfilled, (state, { payload }) => {
      state.status = "getSkills_success";
      state.skills = payload.data.data;
      state.next_page_url = payload.data.next_page_url;
      state.prev_page_url = payload.data.prev_page_url;
      state.current_page = payload.data.current_page;
    });
    builder.addCase(getSkills.rejected, (state) => {
      state.status = "getSkills_rejected";
    });
    builder.addCase(updateSkills.pending, (state) => {
      state.status = "updateSkills_pending";
    });
    builder.addCase(updateSkills.fulfilled, (state, { payload }) => {
      state.status = "updateSkills_success";
      // console.log(payload);
    });
    builder.addCase(updateSkills.rejected, (state) => {
      state.status = "updateSkills_rejected";
    });
    builder.addCase(createSkill.pending, (state) => {
      state.status = "createSkill_pending";
    });
    builder.addCase(createSkill.fulfilled, (state, { payload }) => {
      state.status = "createSkill_success";
      // console.log(payload);
    });
    builder.addCase(createSkill.rejected, (state) => {
      state.status = "createSkill_rejected";
    });
    builder.addCase(deleteSkill.pending, (state) => {
      state.status = "deleteSkill_pending";
    });
    builder.addCase(deleteSkill.fulfilled, (state, { payload }) => {
      state.status = "deleteSkill_success";
      // console.log(payload);
    });
    builder.addCase(deleteSkill.rejected, (state) => {
      state.status = "deleteSkill_rejected";
    });
    builder.addCase(newRole.pending, (state) => {
      state.status = "newRole_pending";
    });
    builder.addCase(newRole.fulfilled, (state, { payload }) => {
      state.status = "newRole_success";
      // console.log(payload);
    });
    builder.addCase(newRole.rejected, (state) => {
      state.status = "newRole_rejected";
    });
    builder.addCase(updateRole.pending, (state) => {
      state.status = "updateRole_pending";
    });
    builder.addCase(updateRole.fulfilled, (state, { payload }) => {
      state.status = "updateRole_success";
      // console.log(payload);
    });
    builder.addCase(updateRole.rejected, (state) => {
      state.status = "updateRole_rejected";
    });
    builder.addCase(deleteRole.pending, (state) => {
      state.status = "deleteRole_pending";
    });
    builder.addCase(deleteRole.fulfilled, (state, { payload }) => {
      state.status = "deleteRole_success";
      // console.log(payload);
    });
    builder.addCase(deleteRole.rejected, (state) => {
      state.status = "deleteRole_rejected";
    });
    builder.addCase(addSkillsRole.pending, (state) => {
      state.status = "addSkillsRole_pending";
    });
    builder.addCase(addSkillsRole.fulfilled, (state, { payload }) => {
      state.status = "addSkillsRole_success";
      // console.log(payload);
    });
    builder.addCase(addSkillsRole.rejected, (state) => {
      state.status = "addSkillsRole_rejected";
    });
  },
});

export default rolesSlice.reducer;

export const { resetStatus } = rolesSlice.actions;
